// @i18n-ignore-file
import { useMemo } from 'react'

import { useLocaleContext } from '@src/components/locale/locale.context'
import type { TAbsoluteLocale } from '@src/config/localisation/locale-config.types'
import { getLocaleConfig } from '@src/config/localisation/statsig-locale-config-constants'
import type { StatsigLocaleDynamicConfig } from '@src/config/localisation/statsig-locale-config.types'
import { mergeLocaleConfig } from '@src/modules/global-state/locale-config.browser'

import { useLocaleConfigContext } from './locale-config.context'

/**
 * Hook to access the Statsig-resolved locale dynamic configuration.
 *
 * ### Resolution order
 *
 * 1. **Primary — per-request Statsig config from React context.** Read from
 *    `useLocaleConfigContext()`, whose value is `pageProps.localeConfig` provided
 *    at the app root. That value was Statsig-resolved on the server
 *    (`getServerSideLocaleConfig` → `getUserAgnosticDynamicConfig(STATSIG_LOCALE_CONFIG_KEY)`,
 *    merged over the server's static defaults). Because it rides the React tree
 *    (props/context), it is **available and identical on the SSR pass and the
 *    client's first render** — so server HTML is correct for crawlers / no-JS
 *    users and there is no hydration mismatch. This replaces the previous
 *    browser-only memory-store read, which was empty on the server and fell back
 *    to the en-US static config (poisoning CA/UK SSR).
 *
 * 2. **Backfill — current-locale static default.** Deep-merge the static default
 *    for `currentLocale` UNDER the context value via `mergeLocaleConfig` (context
 *    wins per key; static default fills only what context is missing). This is a
 *    version-skew guard (a client bundle that knows a key the SSR snapshot didn't
 *    ship yet) and the safe fallback on pages without `getServerSideProps`
 *    (`_error`/404), where the context value is `undefined`.
 *
 * `localeProp` (optional) is the pre-context / hydration-mismatch escape hatch —
 * pass it when the component renders before `LocaleContext` is ready. In every
 * real caller today it equals `useLocaleContext().locale`.
 *
 * @param localeProp - Optional locale to override LocaleContext (useful before context is ready)
 * @returns Merged locale configuration (context + static default for missing keys only)
 */
export const useStatsigDynamicLocaleConfig = (localeProp?: TAbsoluteLocale): StatsigLocaleDynamicConfig => {
  const { locale: contextLocale } = useLocaleContext()
  const currentLocale = localeProp ?? contextLocale
  const resolvedConfig = useLocaleConfigContext()

  return useMemo(
    () =>
      mergeLocaleConfig(
        getLocaleConfig(currentLocale) as unknown as Record<string, unknown>,
        (resolvedConfig ?? {}) as unknown as Record<string, unknown>,
      ) as unknown as StatsigLocaleDynamicConfig,
    [currentLocale, resolvedConfig],
  )
}
